import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, ChevronLeft, ChevronRight, Trash2, Loader2, Images } from "lucide-react";
import { api } from "@/lib/api";
import { abs } from "./ImageSlot";

const input = "rounded-lg border border-white/10 bg-[#0B0B0B] px-3 py-1.5 text-xs text-white outline-none focus:border-[#FF3B3B]/60";

// "Your thumbnails" history with search, sort, date filter and pagination.
export default function ThumbGallery({ refreshKey, onOpen }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const [date, setDate] = useState("");
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    api.ytMyDesigns({ q, sort, date, page, limit: 12 }).then(setD).catch(() => setD({ items: [], total: 0, pages: 1 }));
  }, [q, sort, date, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, refreshKey]);
  const del = async (id) => {
    if (!window.confirm("Delete this thumbnail from your gallery?")) return;
    try { await api.ytDeleteDesign(id); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };
  const set = (fn) => (e) => { fn(e.target.value); setPage(1); };
  return (
    <section className="mt-14" data-testid="yt-gallery">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <h2 className="flex items-center gap-2 font-display text-xl font-black"><Images size={18} className="text-[#FF3B3B]" /> Your thumbnails</h2>
          <p className="text-xs text-white/40" data-testid="yt-gallery-total">{d ? `${d.total} designs` : "Loading…"}</p>
        </div>
        <div className="relative"><Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30" />
          <input value={q} onChange={set(setQ)} placeholder="Search titles, prompts…" data-testid="yt-gallery-search" className={`${input} pl-7`} /></div>
        <select value={sort} onChange={set(setSort)} data-testid="yt-gallery-sort" className={input}>
          <option value="newest">Newest first</option><option value="oldest">Oldest first</option>
        </select>
        <input type="date" value={date} onChange={set(setDate)} data-testid="yt-gallery-date" className={input} />
        {date && <button onClick={() => { setDate(""); setPage(1); }} className="text-xs text-white/50 hover:text-white">Clear date</button>}
      </div>
      {!d ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#FF3B3B]" /></div> : !d.items.length ? (
        <div className="rounded-xl border border-dashed border-white/10 py-12 text-center text-sm text-white/35" data-testid="yt-gallery-empty">No thumbnails yet — your designs will appear here.</div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {d.items.map((g) => (
            <div key={g.id} className="group overflow-hidden rounded-xl border border-white/10 bg-white/[0.02]" data-testid={`yt-gallery-item-${g.id}`}>
              <button onClick={() => onOpen(g)} className="block w-full bg-black" data-testid={`yt-gallery-open-${g.id}`}>
                <img src={abs(g.result_url)} alt="" loading="lazy" className="aspect-video w-full object-contain transition-transform duration-300 group-hover:scale-[1.03]" />
              </button>
              <div className="flex items-center gap-2 px-2.5 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-bold text-white">{g.title || g.instruction || g.prompt || "Untitled"}</div>
                  <div className="text-[10px] text-white/35">{g.kind === "edit" ? "Edited · " : ""}{g.aspect} · {new Date(g.created_at).toLocaleDateString()}</div>
                </div>
                <button onClick={() => del(g.id)} data-testid={`yt-gallery-delete-${g.id}`} className="rounded p-1 text-white/30 hover:text-[#FF3B3B]"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {d && d.pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3 text-xs text-white/50">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="yt-gallery-prev" className="rounded-lg border border-white/10 p-1.5 disabled:opacity-30"><ChevronLeft size={14} /></button>
          <span data-testid="yt-gallery-page">Page {page} / {d.pages}</span>
          <button disabled={page >= d.pages} onClick={() => setPage(page + 1)} data-testid="yt-gallery-next" className="rounded-lg border border-white/10 p-1.5 disabled:opacity-30"><ChevronRight size={14} /></button>
        </div>
      )}
    </section>
  );
}
